/**
 * Base Job Creation Funnel Tracking - Meeting App
 * 
 * This is a lightweight version of the job creation funnel manager
 * specifically for the meeting app. It tracks steps 10-12 of the customer journey:
 * 
 * 10. Meeting Joined - Customer joins the meeting with technician
 * 11. Meeting Completed - Meeting ends successfully
 * 12. Feedback Submitted - Customer provides feedback
 * 
 * This syncs with the main app's funnel tracking via localStorage and LogRocket.
 */

import LogRocket from 'logrocket';
import mixpanel from 'mixpanel-browser';

class JobCreationBaseFunnelManager {
  constructor() {
    this.FUNNEL_NAME = 'job_creation_base';
    this.currentSession = null;
    this.stepTimestamps = {};
    this._loadFunnelState();
  }

  /**
   * Load funnel state from localStorage (shared with main app)
   */
  _loadFunnelState() {
    try {
      const savedState = localStorage.getItem('job_creation_funnel_state');
      if (savedState) {
        const state = JSON.parse(savedState);
        this.currentSession = state.sessionId;
        this.stepTimestamps = state.stepTimestamps || {};
      }
    } catch (error) {
      console.error('Failed to load funnel state:', error);
    }
  }

  /**
   * Save funnel state to localStorage (shared with main app)
   */
  _saveFunnelState() {
    try {
      const state = {
        sessionId: this.currentSession,
        stepTimestamps: this.stepTimestamps,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('job_creation_funnel_state', JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save funnel state:', error);
    }
  }

  /**
   * Get user ID - prioritize real user ID, fallback to visitor ID
   */
  _getUserId(context = {}) {
    // Try to get from context first
    let userId = null;
    
    if (context.user) {
      userId = context.user.id || context.user.email;
    }
    
    // If still no user, try to get visitor ID from localStorage
    if (!userId && typeof localStorage !== 'undefined') {
      userId = localStorage.getItem('geeker_visitor_id');
    }
    
    // Last resort: create a temporary visitor ID
    if (!userId) {
      userId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('geeker_visitor_id', userId);
      }
    }
    
    return userId;
  }

  /**
   * Clean data for LogRocket (only primitives allowed)
   */
  _cleanForLogRocket(data) {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      // Only include primitives and arrays of primitives
      if (value === null || value === undefined) {
        // Skip null/undefined
        return;
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        cleaned[key] = value;
      } else if (Array.isArray(value) && value.every(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
        cleaned[key] = value;
      }
      // Skip objects, functions, etc.
    });
    return cleaned;
  }

  /**
   * Get time elapsed from a previous step
   */
  _getTimeElapsed(fromStep) {
    if (!this.stepTimestamps[fromStep]) return null;
    return Date.now() - this.stepTimestamps[fromStep];
  }

  /**
   * Step 10: Track when customer joins the meeting
   */
  trackMeetingJoined(context = {}) {
    this.stepTimestamps.meeting_joined = Date.now();
    this._saveFunnelState();

    const userId = this._getUserId(context);
    
    const stepData = this._cleanForLogRocket({
      userId: String(userId),
      jobId: String(context.jobId || ''),
      technicianId: String(context.technicianId || ''),
      meetingPlatform: context.meetingPlatform || 'jitsi',
      joinTime: new Date().toISOString(),
      timeFromTechAcceptance: this._getTimeElapsed('tech_accepted'),
      timeFromJobPosted: this._getTimeElapsed('job_posted'),
      deviceType: context.deviceType || 'unknown',
      browser: context.browser || navigator.userAgent,
      isGuest: String(context.user?.email === 'guest@geeker.co' || !context.user),
    });

    // Track with VWO - check if VWO is loaded and push is a function
    if (window._vwo_code && typeof window._vwo_code.push === 'function') {
      window._vwo_code.push(['event', 'Job Creation - Meeting Joined', stepData]);
    }

    // Track with LogRocket
    if (window.LogRocket) {
      LogRocket.track('Job Creation - Meeting Joined', stepData);
    }

    // Track with Mixpanel
    if (window.mixpanel) {
      window.mixpanel.track('Job Creation - Meeting Joined', stepData);
    }

    console.log('📊 Step 10: Meeting Joined', stepData);
  }

  /**
   * Step 11: Track when meeting is completed
   */
  trackMeetingCompleted(context = {}) {
    this.stepTimestamps.meeting_completed = Date.now();
    this._saveFunnelState();

    const userId = this._getUserId(context);
    
    const stepData = this._cleanForLogRocket({
      userId: String(userId),
      jobId: String(context.jobId || ''),
      technicianId: String(context.technicianId || ''),
      meetingDuration: context.meetingDuration || 0,
      meetingDurationFormatted: context.meetingDurationFormatted || '',
      completionTime: new Date().toISOString(),
      timeFromMeetingJoin: this._getTimeElapsed('meeting_joined'),
      totalTimeFromJobCreation: this._getTimeElapsed('category_selected'),
      deviceType: context.deviceType || 'unknown',
      wasSuccessful: String(context.wasSuccessful !== false),
      endedBy: String(context.endedBy || 'unknown'),
      isGuest: String(context.user?.email === 'guest@geeker.co' || !context.user),
    });

    // Track with VWO - check if VWO is loaded and push is a function
    if (window._vwo_code && typeof window._vwo_code.push === 'function') {
      window._vwo_code.push(['event', 'Job Creation - Meeting Completed', stepData]);
    }

    // Track with LogRocket
    if (window.LogRocket) {
      LogRocket.track('Job Creation - Meeting Completed', stepData);
    }

    // Track with Mixpanel
    if (window.mixpanel) {
      window.mixpanel.track('Job Creation - Meeting Completed', stepData);
    }

    console.log('📊 Step 11: Meeting Completed', stepData);
  }

  /**
   * Step 12: Track when customer submits feedback
   */
  trackFeedbackSubmitted(context = {}) {
    this.stepTimestamps.feedback_submitted = Date.now();
    this._saveFunnelState();

    const userId = this._getUserId(context);
    
    const stepData = this._cleanForLogRocket({
      userId: String(userId),
      jobId: String(context.jobId || ''),
      technicianId: String(context.technicianId || ''),
      rating: context.rating || 0,
      wasIssueSolved: String(context.wasIssueSolved || false),
      feedbackLength: context.feedbackText?.length || 0,
      submissionTime: new Date().toISOString(),
      timeFromMeetingCompletion: this._getTimeElapsed('meeting_completed'),
      totalJourneyTime: this._getTimeElapsed('category_selected'),
      deviceType: context.deviceType || 'unknown',
      isGuest: String(context.user?.email === 'guest@geeker.co' || !context.user),
    });

    // Track with VWO - check if VWO is loaded and push is a function
    if (window._vwo_code && typeof window._vwo_code.push === 'function') {
      window._vwo_code.push(['event', 'Job Creation - Feedback Submitted', stepData]);
    }

    // Track with LogRocket
    if (window.LogRocket) {
      LogRocket.track('Job Creation - Feedback Submitted', stepData);
    }

    // Track with Mixpanel
    if (window.mixpanel) {
      window.mixpanel.track('Job Creation - Feedback Submitted', stepData);
    }

    console.log('📊 Step 12: Feedback Submitted', stepData);
    
    // Mark funnel as complete
    this.completeFunnel(context);
  }

  /**
   * Complete the funnel - customer successfully went through all 12 steps
   */
  completeFunnel(context = {}) {
    console.log('🎉 FUNNEL COMPLETED - Full customer journey from job creation to feedback!', context);
    
    const userId = this._getUserId(context);
    
    // Calculate total time from start to completion
    const startTime = this.stepTimestamps['category_selected'];
    const endTime = Date.now();
    
    let totalDuration = null;
    if (startTime) {
      totalDuration = Math.floor((endTime - startTime) / 1000); // in seconds
    }

    const completionData = this._cleanForLogRocket({
      userId: String(userId),
      jobId: String(context.jobId || ''),
      technicianId: String(context.technicianId || ''),
      totalDuration: totalDuration,
      completionTimestamp: new Date().toISOString(),
      completedSteps: Object.keys(this.stepTimestamps).length,
      isGuest: String(context.user?.email === 'guest@geeker.co' || !context.user),
    });

    // Track completion in VWO
    if (window._vwo_code && typeof window._vwo_code.push === 'function') {
      window._vwo_code.push(['event', 'Job Creation - Funnel Completed', completionData]);
    }

    // Track completion in LogRocket
    if (window.LogRocket) {
      LogRocket.track('Job Creation - Funnel Completed', completionData);
    }

    // Track completion in Mixpanel
    if (window.mixpanel) {
      window.mixpanel.track('Job Creation - Funnel Completed', completionData);
    }

    console.log('📊 Funnel Completion Tracked', completionData);

    // Clear funnel state
    localStorage.removeItem('job_creation_funnel_state');
    this.currentSession = null;
    this.stepTimestamps = {};
  }

  /**
   * Get current funnel progress
   */
  getFunnelProgress() {
    return {
      sessionId: this.currentSession,
      completedSteps: Object.keys(this.stepTimestamps).length,
      stepTimestamps: { ...this.stepTimestamps }
    };
  }
}

// Create singleton instance
const jobCreationBaseFunnelManager = new JobCreationBaseFunnelManager();

// Make it globally available for debugging
if (typeof window !== 'undefined') {
  window.jobCreationBaseFunnelManager = jobCreationBaseFunnelManager;
}

export default jobCreationBaseFunnelManager;
